import type { CollectionProps } from '@react-aria/collections';
import { focusSafely, useKeyboard } from '@react-aria/interactions';
import {
  filterDOMProps,
  mergeRefs,
  useLayoutEffect,
  useObjectRef,
} from '@react-aria/utils';
import type {
  AriaLabelingProps,
  DOMProps,
  LinkDOMProps,
  forwardRefType,
} from '@react-types/shared';
import { assert } from 'emery';
import { omit } from 'lodash-es';
import type {
  AnimatePresenceProps,
  EasingDefinition,
  Variants,
} from 'motion/react';
import {
  AnimatePresence,
  motion,
  usePresenceData,
  useReducedMotion,
} from 'motion/react';
import type { ForwardedRef, HTMLAttributes, ReactNode } from 'react';
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Key, TabListProps, TabProps } from 'react-aria-components';
import {
  Collection,
  DEFAULT_SLOT,
  Header,
  Heading,
  Provider,
  Tab,
  TabList,
  TabListStateContext,
  TabPanel,
  Tabs,
} from 'react-aria-components';
import type { TabListState } from 'react-stately';

import { resolveMaybeFn } from '@infinex-private/ts-utils/function';
import { easing } from '@infinex/ui-constants';
import { ArrowLeftIcon } from '@infinex/ui-icons';
import type { UnsafeStyleProps } from '@infinex/ui-styles';
import { cn } from '@infinex/ui-styles';
import { querySelector } from '@infinex/ui-utils/dom';
import { isReactText, useMountEffect } from '@infinex/ui-utils/react';

import { DialogRouterVars, DialogVars } from './constants';
import {
  DialogHeaderButton,
  DismissButton,
  includeDismissButton,
} from './Dialog';
import type {
  TransitionDirection,
  TransitionType,
  DialogRouteState,
} from './DialogRouteState';
import {
  DIRECTION,
  NAVIGATION,
  DialogRouteStateContext,
} from './DialogRouteState';
import { useDialogState } from './useDialogState';

import { ButtonContext } from '../button';
import { AnimateHeight } from '../layout';

const currentDirection = NAVIGATION.select((context) => context.direction);
const currentStack = NAVIGATION.select((context) => context.stack);
const currentTransition = NAVIGATION.select((context) => context.transition);
const navigationIsReady = NAVIGATION.select((context) => context.isReady);

// Public `DialogRouter`
// -----------------------------------------------------------------------------

export type DialogRouterProps = {
  /**
   * A render function that receives the current route config and returns the
   * appropriate `DialogRoute` component for the matching ID.
   */
  children: ReactNode;
  /**
   * In cases where the initial route is not the first route that should be
   * displayed, you can provide a custom stack of route IDs to be used as the
   * initial navigation history stack.
   */
  initialStack?: Key[];
  /**
   * Whether to allow pop navigation. A function can be provided to customise
   * the behaviour based on the current route ID.
   *
   * @default true
   */
  allowPopNavigation?: boolean | ((id: Key) => boolean);
  /**
   * A callback that is called when the route changes.
   */
  onRouteChange?: (routeId: Key) => void;
  /**
   * The transition type to use for animating each route and its title.
   *
   * @default cross-fade
   */
  transition?: TransitionType | ((id: Key) => TransitionType);
} & DOMProps &
  UnsafeStyleProps;

/**
 * Manages route-like navigation within a dialog, providing animated
 * transitions between routes.
 *
 * [Taurus docs: `DialogRouter`](https://ui-sb.app.infinex.dev/?path=/docs/components-dialogrouter--docs)
 *
 * @see {@link DialogRouterHeader}
 * @see {@link DialogRouterBody}
 */
export const DialogRouter = forwardRef<HTMLDivElement, DialogRouterProps>(
  function DialogRouter(props, ref) {
    return (
      <Tabs
        {...props}
        ref={ref}
        className={cn('contents', props.className)}
        style={props.style}
        defaultSelectedKey={props.initialStack?.at(-1)}
      >
        <DeferredInner props={props} />
        {/* <Provider
          values={[
            [ImageContext, { isHidden: true }],
            [HeaderContext, { isHidden: true }],
            [HeadingContext, { isHidden: true }],
            [ContentContext, { isHidden: true }],
            [FooterContext, { isHidden: true }],
          ]}
        >
          <DeferredInner props={props} />
        </Provider> */}
      </Tabs>
    );
  }
);

// Wait for tab state to be ready before rendering the inner component.
function DeferredInner({ props }: { props: DialogRouterProps }) {
  const tabState = useContext(TabListStateContext);
  return tabState && tabState.selectedKey ? (
    <DialogRouterInner props={props} tabState={tabState} />
  ) : (
    props.children
  );
}

// The router inner component initialises:
// - route state
// - header and footer height CSS vars
// - keyboard handlers
function DialogRouterInner({
  props,
  tabState,
}: {
  props: DialogRouterProps;
  tabState: TabListState<object>;
}) {
  const { allowPopNavigation, initialStack, onRouteChange, transition } = props;
  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);

  const initProps = useMemo(() => {
    return { allowPopNavigation, initialStack, onRouteChange, transition };
  }, [allowPopNavigation, initialStack, onRouteChange, transition]);
  const routeState = useInitRouteState(initProps, tabState);

  const ref = useRef<HTMLDivElement>(null);
  const measureHeights = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const noLayout = new Set(['absolute', 'fixed']);

    const header = querySelector(el, ':scope > [slot="header"]');
    if (header && !noLayout.has(getComputedStyle(header).position)) {
      setHeaderHeight(header.clientHeight);
    } else {
      setHeaderHeight(0);
    }
    const footer = querySelector(el, ':scope > [slot="footer"]');
    if (footer && !noLayout.has(getComputedStyle(footer).position)) {
      setFooterHeight(footer.clientHeight);
    } else {
      setFooterHeight(0);
    }
  }, []);
  useLayoutEffect(
    () => queueMicrotask(measureHeights),
    [measureHeights, routeState.currentRouteId]
  );

  const { keyboardProps } = useKeyboard({
    isDisabled: !routeState.isPopAllowed,
    onKeyDown: (event) => {
      if (event.isDefaultPrevented() || event.key !== 'Escape') {
        return;
      }

      routeState.pop();
    },
  });

  const focusCurrentPanel = useCallback(() => {
    const panel = querySelector(
      ref.current,
      '[role="tabpanel"]:not([data-inert])'
    );
    if (!panel || isFocusWithin(panel)) return;
    panel.tabIndex = -1;
    focusSafely(panel);
  }, []);

  useMountEffect(() => {
    if (routeState.stack.length === 1) focusCurrentPanel();
  });
  useEffect(
    () => queueMicrotask(focusCurrentPanel),
    [tabState?.selectionManager.focusedKey, focusCurrentPanel]
  );

  const dialog = useDialogState();

  return (
    <div
      {...keyboardProps}
      ref={ref}
      className="contents"
      style={DialogRouterVars.apply((vars) => ({
        bodyHeight: `calc(${DialogVars.maxHeight} - ${vars.headerHeight} - ${vars.footerHeight})`,
        footerHeight,
        headerHeight,
      }))}
    >
      <DialogRouteStateContext.Provider value={routeState}>
        <Provider
          values={[
            [
              ButtonContext,
              {
                slots: {
                  [DEFAULT_SLOT]: {},
                  next: {
                    onPress: () =>
                      routeState.nextRouteId &&
                      routeState.push(routeState.nextRouteId),
                    isDisabled: !routeState.nextRouteId,
                  },
                  prev: {
                    onPress: () => routeState.isPopAllowed && routeState.pop(),
                    isDisabled: !routeState.isPopAllowed,
                  },
                  // since overriding slots, we need to re-add the RAC "close" slot
                  close: {
                    onPress: () => dialog.close(),
                  },
                },
              },
            ],
          ]}
        >
          {props.children}
        </Provider>
      </DialogRouteStateContext.Provider>
    </div>
  );
}

function isFocusWithin(el: HTMLElement) {
  return el.contains(document.activeElement);
}

// Public Header
// -----------------------------------------------------------------------------

export type DialogRouterHeaderProps<T extends object> = Omit<
  TabListProps<T>,
  'dependencies' | 'className' | 'style'
> &
  UnsafeStyleProps;

/**
 * The header component renders the current route title and related actions for
 * the `DialogRouter` component.
 *
 * @see {@link DialogRouter}
 */
export const DialogRouterHeader = /*#__PURE__*/ (forwardRef as forwardRefType)(
  function DialogRouterHeader<T extends object>(
    props: DialogRouterHeaderProps<T>,
    forwardedRef: ForwardedRef<HTMLDivElement>
  ) {
    return (
      <>
        <TabList {...props} className="hidden" key="tablist" />
        <DialogRouterHeaderInner {...props} elementRef={forwardedRef} />
      </>
    );
  }
);

type DialogRouterHeaderInnerProps<T extends object> =
  DialogRouterHeaderProps<T> & {
    elementRef: ForwardedRef<HTMLDivElement>;
  };

function DialogRouterHeaderInner<T extends object>(
  props: DialogRouterHeaderInnerProps<T>
) {
  const state = useContext(DialogRouteStateContext);
  const dialog = useDialogState();
  const maskImage = getMaskImage({
    isDismissable: dialog.isDismissable,
    isPopAllowed: state?.isPopAllowed,
  });

  const buttonTransitionType =
    currentTransition.get() === 'instant' ? 'instant' : 'fade';

  // Get the current route item here and pass it along to the internal title
  // component as props, ensuring that the elements are correct by key.
  // Otherwise, if we get the current item inside the title component, the
  // exiting element will be the same as the entering element.
  const selectedItem = state?.selectedItem;

  return (
    <Header
      {...filterDOMProps(props)}
      ref={props.elementRef}
      slot="header"
      className={cn(
        'relative flex shrink-0 items-baseline justify-center gap-4 self-stretch',
        props.className,
        DialogVars.tw(
          'gap-(--Dialog-header-gap)',
          'mx-(--Dialog-header-gutter)',
          'pt-(--Dialog-header-gutter)'
        )
      )}
      style={props.style}
    >
      <AnimatePresence initial={false}>
        {state?.isPopAllowed && (
          <AnimatePresenceChild
            key="back-button"
            type={buttonTransitionType}
            className="absolute left-0 z-10"
          >
            <DialogHeaderButton aria-label="back" slot="prev">
              <ArrowLeftIcon />
            </DialogHeaderButton>
          </AnimatePresenceChild>
        )}
      </AnimatePresence>

      <div className="grid min-h-8 flex-1" style={{ maskImage }}>
        <AnimatePresence
          custom={currentDirection.get()}
          initial={false}
          mode="popLayout"
        >
          <InternalRouteTitle
            key={state?.currentRouteId}
            className={selectedItem?.props.className}
            style={selectedItem?.props.style}
          >
            {selectedItem?.rendered}
          </InternalRouteTitle>
        </AnimatePresence>
      </div>

      <AnimatePresence initial={false}>
        {includeDismissButton(dialog) && (
          <AnimatePresenceChild
            key="dismiss-button"
            type={buttonTransitionType}
            className="absolute right-0 z-10"
          >
            <DismissButton />
          </AnimatePresenceChild>
        )}
      </AnimatePresence>
    </Header>
  );
}

function getMaskImage(props: {
  isDismissable?: boolean;
  isPopAllowed?: boolean;
}): string | undefined {
  const { isPopAllowed, isDismissable } = props;

  if (isPopAllowed && isDismissable) {
    return `linear-gradient(to right, transparent ${DialogVars.headerButton}, white ${DialogVars.headerButtonSpacer}, white calc(100% - ${DialogVars.headerButtonSpacer}), transparent calc(100% - ${DialogVars.headerButton}))`;
  } else if (isPopAllowed) {
    return `linear-gradient(to right, transparent ${DialogVars.headerButton}, white ${DialogVars.headerButtonSpacer})`;
  } else if (isDismissable) {
    return `linear-gradient(to left, transparent ${DialogVars.headerButton}, white ${DialogVars.headerButtonSpacer})`;
  }
}

export type DialogRouteTitleProps = Omit<TabProps, keyof LinkDOMProps> & {
  /**
   * A string representation of the item's contents, used when the title
   * contains non-string content.
   */
  textValue?: string;
};

/**
 * Provides the title for an individual item within a dialog router. Each route
 * title should be rendered inside the `DialogRouterHeader` component.
 *
 * @see {@link DialogRouter}
 * @see {@link DialogRouterHeader}
 */
export function DialogRouteTitle(props: DialogRouteTitleProps) {
  if (props.textValue && props.textValue.trim() === '') {
    throw new Error(
      `Why do you hate blind people?\n\nDialogRouteTitle: \`textValue\` must not be empty or contain only whitespace. Provide meaningful text for screen readers.`
    );
  }

  assert(
    Boolean(props.textValue || isReactText(props.children)),
    'When using a non-string title, you must provide a `textValue` prop.'
  );

  const textValue =
    props.textValue ||
    (isReactText(props.children) ? props.children : undefined);

  // @ts-expect-error — while `textValue` is not a valid prop for the `Tab`
  // component, it is a collection component so this works.
  return <Tab {...props} textValue={textValue} />;
}

/**
 * @private Internal component for rendering the current route title.
 */
const InternalRouteTitle = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(function InternalRouteTitle(props, ref) {
  const { variants, transition } = useTransitionProps();

  return (
    <motion.div
      {...variants}
      ref={ref}
      // min-w-0/max-w-full: as a flex item of the header this wrapper's
      // implicit min-width is its content width, so the Heading's `truncate`
      // below could never engage — long titles overflowed instead.
      className="grid max-w-full min-w-0 place-items-center"
      transition={transition}
    >
      <Heading
        slot="title"
        className={cn(
          'text-emphasis min-w-0 truncate text-center text-base font-semibold',
          props.className
        )}
        style={props.style}
      >
        {props.children}
      </Heading>
    </motion.div>
  );
});

// Public Body
// -----------------------------------------------------------------------------

export type DialogRouterBodyProps<T extends object> = CollectionProps<T> &
  DOMProps &
  UnsafeStyleProps;

/**
 * The `DialogRouterBody` contains the current route content for the
 * `DialogRouter` component.
 *
 * @see {@link DialogRoute}
 */
export const DialogRouterBody = /*#__PURE__*/ (forwardRef as forwardRefType)(
  function DialogRouterBody<T extends object>(
    props: DialogRouterBodyProps<T>,
    ref: ForwardedRef<HTMLDivElement>
  ) {
    const collection = <Collection {...props} />;

    // Wait for the tab state to be ready before rendering the inner component.
    return useIsStateReady() ? (
      <DialogRouterBodyInner elementRef={ref} {...props}>
        {collection}
      </DialogRouterBodyInner>
    ) : (
      collection
    );
  }
);

type DialogRouterBodyInnerProps<T extends object> = Omit<
  DialogRouterBodyProps<T>,
  'children'
> & {
  elementRef: ForwardedRef<HTMLDivElement>;
  children: ReactNode;
};

function DialogRouterBodyInner<T extends object>(
  props: DialogRouterBodyInnerProps<T>
) {
  const { children, className, style, ...otherProps } = props;
  const { transition } = useTransitionProps();
  const dialog = useDialogState();

  const bodyElement = (
    <div
      {...filterDOMProps(otherProps)}
      ref={props.elementRef}
      className={cn(
        'flex-col-fill contain-paint',
        DialogRouterVars.tw('max-h-(--DialogRouter-body-height)'),
        className
      )}
      style={style}
    >
      {children}
    </div>
  );

  // sheets have a fixed height, don't animate changes to their content
  if (dialog.type === 'sheet') {
    return bodyElement;
  }

  return (
    <AnimateHeight duration={transition.duration} ease={transition.ease}>
      {bodyElement}
    </AnimateHeight>
  );
}

// Public `DialogRoute`
// -----------------------------------------------------------------------------

export type DialogRouteProps = {
  /**
   * The content of the route. A function can be provided to render dynamic
   * content based on route state. However, this may cause performance issues
   * and is not recommended.
   */
  children: ((state: DialogRouteState<object>) => ReactNode) | ReactNode;
  /**
   * A unique identifier for the route.
   */
  id: string;
} & AriaLabelingProps &
  UnsafeStyleProps;

/**
 * Provides the content for an individual item within a dialog router. Each
 * route should be rendered inside the `DialogRouterBody` component.
 *
 * @see {@link DialogRouter}
 * @see {@link DialogRouterBody}
 */
export const DialogRoute = /*#__PURE__*/ (forwardRef as forwardRefType)(
  function DialogRoute(
    props: DialogRouteProps,
    forwardedRef: ForwardedRef<HTMLDivElement>
  ) {
    const state = useContext(DialogRouteStateContext);

    return (
      <AnimatePresence
        custom={currentDirection.get()}
        initial={false}
        mode="popLayout"
      >
        {state?.currentRouteId === props.id && (
          <DialogRouteInner
            {...props}
            elementRef={forwardedRef}
            state={state}
          />
        )}
      </AnimatePresence>
    );
  }
);

type DialogRouteInnerProps = DialogRouteProps & {
  elementRef: ForwardedRef<HTMLDivElement>;
  state: DialogRouteState<object>;
};

const MotionTabPanel = motion.create(TabPanel);

const DialogRouteInner = /*#__PURE__*/ (forwardRef as forwardRefType)(
  function DialogRouteInner(
    props: DialogRouteInnerProps,
    forwardedRef: ForwardedRef<HTMLDivElement>
  ) {
    const { children, state, className, style, ...otherProps } = props;

    const domRef = useObjectRef(mergeRefs(forwardedRef, props.elementRef));
    const { variants, transition } = useTransitionProps();

    return (
      <MotionTabPanel
        {...otherProps}
        {...variants}
        transition={transition}
        ref={domRef}
        shouldForceMount
        className={cn('flex-col-fill outline-hidden', className)}
        style={style}
      >
        {resolveMaybeFn(children, state)}
      </MotionTabPanel>
    );
  }
);

// Private `AnimatePresenceChild`
// -----------------------------------------------------------------------------

export type DialogRouteTransitionProps = Pick<
  AnimatePresenceProps,
  'initial' | 'onExitComplete' | 'mode'
> &
  AnimatePresenceChildProps;

/**
 * A state-aware wrapper around `motion.div` that manages the presence of its
 * child element using the "motion/react" `AnimatePresence` component. Used for
 * animating elements rendered outside of the dialog router's body.
 *
 * @see https://motion.dev/docs/react-animate-presence
 */
export const DialogRouteTransition = forwardRef<
  HTMLDivElement,
  DialogRouteTransitionProps
>(function DialogRouteTransition(props, ref) {
  const { initial = false, onExitComplete, mode, ...transitionProps } = props;

  return (
    <AnimatePresence
      initial={initial}
      mode={mode}
      onExitComplete={onExitComplete}
    >
      {props.children && (
        <AnimatePresenceChild ref={ref} {...transitionProps} />
      )}
    </AnimatePresence>
  );
});

export type AnimatePresenceChildProps = {
  /**
   * The animated element to conditionally render.
   */
  children: ReactNode;
  /**
   * How the element should animate when it enters and exits. Inherited from the
   * `transition` prop provided to the `DialogRouter` component.
   *
   * @default cross-fade
   */
  type?: TransitionType;
} & DOMProps &
  UnsafeStyleProps;

/**
 * @private A thin wrapper around `motion.div` that implements the "transition"
 * types supported by the `DialogRouter` component.
 */
export const AnimatePresenceChild = forwardRef<
  HTMLDivElement,
  AnimatePresenceChildProps
>(function AnimatePresenceChild(props, ref) {
  const { children, type, className, style, ...otherProps } = props;
  const { variants, transition } = useTransitionProps({ type });

  return (
    <motion.div
      {...motionSafeDOMProps(filterDOMProps(otherProps))}
      {...variants}
      ref={ref}
      transition={transition}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  );
});

// reduced motion != no animation, allow fade
const REDUCED_MOTION = {
  instant: 'instant',
  fade: 'fade',
  'cross-fade': 'fade',
  slide: 'fade',
} as const;

// Utils
// -----------------------------------------------------------------------------

const offsetX = { instant: 0, fade: 0, 'cross-fade': 15, slide: 100 } as const;

type Timing = { duration: number; ease: EasingDefinition };
type UseTransitionResult = { transition: Timing; variants: Variants };

function useTransitionProps(
  options: { type?: TransitionType } = {}
): UseTransitionResult {
  const prefersReducedMotion = useReducedMotion();
  const presenceData = usePresenceData() as TransitionDirection;
  const defaultType = options.type ?? currentTransition.get() ?? 'cross-fade';
  const type = prefersReducedMotion ? REDUCED_MOTION[defaultType] : defaultType;
  const numericDirection = DIRECTION[presenceData];

  return {
    transition: {
      duration: type === 'instant' ? 0 : 0.4,
      ease: easing.easeOutSmooth,
    } satisfies Timing,
    variants: {
      initial: { opacity: 0, x: `${numericDirection * offsetX[type]}%` },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: `${numericDirection * offsetX[type] * -1}%` },
    } as const satisfies Variants,
  };
}

// Context
// -----------------------------------------------------------------------------

type InitProps = Pick<
  DialogRouterProps,
  'allowPopNavigation' | 'initialStack' | 'onRouteChange' | 'transition'
>;

function useInitRouteState(
  props: InitProps,
  tabState: TabListState<object>
): DialogRouteState<object> {
  const {
    allowPopNavigation = true,
    initialStack,
    onRouteChange,
    transition: transitionProp = 'cross-fade',
  } = props;

  const firstKey = tabState.collection.getFirstKey();
  const keySet = new Set(tabState.collection.getKeys());

  // Initialise the route store with props and tab state values.
  useEffect(() => {
    if (navigationIsReady.get()) return;

    const stack = initialStack || (firstKey ? [firstKey] : []);

    NAVIGATION.send({
      type: 'initialise',
      stack,
      transition:
        typeof transitionProp === 'function'
          ? transitionProp(stack[stack.length - 1])
          : transitionProp,
    });
  }, [initialStack, firstKey, transitionProp]);

  // Sync external state with changes to the history stack.
  useMountEffect(() => {
    const stackSubscription = currentStack.subscribe((snapshot) => {
      const lastKey = snapshot.at(-1);
      if (lastKey) {
        // Sync RAC tab state with the history store.
        tabState.setSelectedKey(lastKey);
        // Call consumer route change handler here rather than passing to the
        // `Tabs` "onSelectionChange" prop to avoid calling on mount.
        onRouteChange?.(lastKey);
      }
    });

    return () => {
      NAVIGATION.send({ type: 'reset' });
      stackSubscription.unsubscribe();
    };
  });

  return {
    get isPopAllowed() {
      if (currentStack.get().length <= 1) {
        return false;
      }
      if (typeof allowPopNavigation === 'function') {
        return allowPopNavigation(this.currentRouteId);
      }

      return allowPopNavigation;
    },
    get routeIDs() {
      return tabState.collection.getKeys() ?? [];
    },
    get currentRouteId() {
      return tabState.selectedKey ?? '';
    },
    get nextRouteId() {
      return tabState.collection.getKeyAfter(this.currentRouteId) ?? null;
    },
    get prevRouteId() {
      return tabState.collection.getKeyBefore(this.currentRouteId) ?? null;
    },
    get selectedItem() {
      return tabState.selectedItem ?? null;
    },
    get stack() {
      return currentStack.get();
    },

    push(id: Key) {
      if (!keySet.has(id)) {
        console.warn(
          `WARNING: Invalid attempt to push the current route "${id}" onto the stack.`
        );
        return;
      }
      if (id === this.currentRouteId) {
        console.warn(
          `WARNING: Invalid attempt to push the current route "${id}" onto the stack.`
        );
        return;
      }

      NAVIGATION.send({ type: 'push', id });
    },
    pop() {
      if (currentStack.get().length <= 1) {
        console.warn(
          `WARNING: Invalid attempt to pop the last route "${currentStack.get().at(-1)}" from the stack.`
        );
        return;
      }

      NAVIGATION.send({ type: 'pop' });
    },
    replace(id: string, direction: 'forward' | 'backward' = 'forward') {
      if (!keySet.has(id)) {
        throw new Error(`Invalid attempt to replace unknown route: "${id}".`);
      }
      if (id === this.currentRouteId) {
        console.warn(
          `WARNING: Invalid attempt to replace the current route "${id}" with itself.`
        );
        return;
      }

      NAVIGATION.send({ type: 'replace', id, direction });
    },
  };
}

function useIsStateReady() {
  return !!(
    useContext(TabListStateContext)?.selectedKey && navigationIsReady.get()
  );
}

// Helpers ---------------------------------------------------------------------

/**
 * Motion components e.g. `motion.div` alter a handful of native HTML attributes
 * (naughty!), which conflict with `React.DOMAttributes` types making prop spreading problematic.
 *
 * @example
 * ```tsx
 * <motion.div {...motionSafeDOMProps(filterDOMProps(props))} />
 * ```
 */
function motionSafeDOMProps<P extends object>(props: P) {
  return omit(props, 'onAnimationStart', 'onDragStart', 'onDragEnd', 'onDrag');
}
